import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useQuery } from '@apollo/client';
import clsx from 'clsx';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import {
  useDeactivateSoScheduleMutation,
  useEditBulkDonorSchedulesMutation,
} from '@/app/services/donorSchedules';
import { DonorSchedulingActions } from '@/app/slices/donorScheduling';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  BILL_ACCOUNT_CANING_OPTION_VALUES,
  LOAD_DONOR_PARTNERS_OPTION_VALUES,
} from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePastDueStatus } from '@/hooks/usePastDueStatus';
import { DonorSchedule } from '@/rtk-query.gql';
import { queryClient } from '@/services';
import { useUpsertDonorSchedules } from '@/services/donor-scheduling/hooks';
import { partnershipsOptions } from '@/services/partnerships';
import { UPDATE_BILL_ACCOUNT_CANING_GROUPS } from '@/types.gql';
import { TOP_REVISIONS_BY_FERTILIZATION_SITE } from '@/types.gql';
import { runNewWorkflow, WORKFLOW } from '@/utils';
import { getDifferencesOnly } from '@/utils';
import { normalizeSubFamily } from '@/utils/subfamilyValues';

import { tabIndexMap, useAllowedNullFields } from '../../constants';
import { useMissingFields } from '../../hooks/useMissingFields';
import { removeNulls } from '../../util/util';
import {
  DonorScheduleFormEmbryos,
  DonorScheduleFormGeneral,
  DonorScheduleFormNotes,
  DonorScheduleFormSchedules,
  DonorScheduleFormSemen,
} from '../donor-schedule-form';
import { BULK_EDIT_FIELD_LABEL_KEYS } from '../donor-schedule-form/constants';
import { MissingFieldsDialog } from '../toolbar/MissingFieldsDialog';

const EXPORT_NO_VALUE = 100000000;

type EditDonorScheduleProps = {
  className?: string;
};

export const EditDonorSchedule = ({
  className = '',
}: EditDonorScheduleProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [params, setParams] = useSearchParams();

  const id = params.get('id');
  const isRevisionMode = params.get('revision') === 'true';

  const [isPanelCollapsed, setIsPanelCollapsed] = useState(true);
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();
  const [prevBillAccountCaningOption, setPrevBillAccountCaningOption] =
    useState<number | undefined>();

  const [upsertDonorSchedules] = useUpsertDonorSchedules();
  const [editBulkDonorSchedules] = useEditBulkDonorSchedulesMutation();

  const [updateBillAccountCaningGroups] = useMutation(
    UPDATE_BILL_ACCOUNT_CANING_GROUPS,
  );
  const [deactivateSoSchedule] = useDeactivateSoScheduleMutation();
  const isTabViewShadingEnabled = useFeatureFlag('Tab_View_Shading');
  const isDsDefaultSoEnabled = useFeatureFlag('DS_Default_SO');
  const isNoEmbryoForExportEnabled = useFeatureFlag('No_Embryo_For_Export');
  const isPreventDonorSireMismatchEnabled = useFeatureFlag(
    'Prevent_Donor_Schedule_Save_When_Donor_Sire_Do_Not_Match',
  );
  const allowedNullFields = useAllowedNullFields();
  const tabViewClassName = clsx(
    'donor-schedule-tabs',
    isTabViewShadingEnabled && 'tab-view-shading',
    className,
  );

  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [soScheduleDeactivated, setSoScheduleDeactivated] = useState(false);

  const { partnership } = useAppSelector((state) => state.partnerships);
  const { selectedDonorSchedules, workingDonorSchedule, mergedDonorSchedules } =
    useAppSelector((state) => state.donorScheduling);

  const [unlockAll, setUnlockAll] = useState<boolean | null>(
    workingDonorSchedule.soSchedule === null,
  );

  const [lastSavedState, setLastSavedState] = useState<DonorSchedule | null>(
    workingDonorSchedule,
  );

  const { data: topRevisions } = useQuery(TOP_REVISIONS_BY_FERTILIZATION_SITE, {
    variables: {
      fertilizationSiteId: workingDonorSchedule?.fertilizationSiteId,
      date: workingDonorSchedule?.date,
    },
    skip:
      !workingDonorSchedule?.fertilizationSiteId ||
      isRevisionMode ||
      !workingDonorSchedule?.date,
  });

  const [revisionComment, setRevisionComment] = useState('');
  const [revisionNumber, setRevisionNumber] = useState<{
    previousRevision: string;
    currentRevision: string;
  }>({ previousRevision: '', currentRevision: '' });

  const getInitialTabIndex = () => {
    const revision = params.get('revision') === 'true';
    const tab = params.get('tab');

    if (revision && tab) {
      const targetIndex = tabIndexMap[tab];
      return targetIndex !== undefined ? targetIndex : 0;
    }
    return 0;
  };

  const [activeIndex, setActiveIndex] = useState(getInitialTabIndex);

  const selectedCount = useMemo(
    () => selectedDonorSchedules?.length ?? 0,
    [selectedDonorSchedules],
  );

  const { getMissingFields } = useMissingFields([workingDonorSchedule]);

  const showDialog = useMemo(() => params.get('edit') === 'true', [params]);
  const editingMultiple = useMemo(
    () => params.get('multiple') === 'true',
    [params],
  );

  const initialRevisionComment = workingDonorSchedule?.revisionComment ?? '';
  useEffect(() => {
    if (revisionComment.length > 0) {
      return;
    }

    if (initialRevisionComment.length === 0) {
      return;
    }

    setRevisionComment(initialRevisionComment);
  }, [initialRevisionComment, revisionComment.length]);

  const topRevisionFromServer =
    (topRevisions?.topRevisionsByFertilizationSite as string | null) ?? null;
  useEffect(() => {
    if (!topRevisionFromServer) {
      return;
    }

    setRevisionNumber((prev) => {
      if (prev.previousRevision.length > 0) {
        return prev;
      }

      return {
        ...prev,
        previousRevision: topRevisionFromServer,
      };
    });
  }, [topRevisionFromServer]);

  const initialCurrentRevision = workingDonorSchedule?.revision ?? '';
  useEffect(() => {
    if (initialCurrentRevision.length === 0) {
      return;
    }

    setRevisionNumber((prev) => {
      if (prev.currentRevision.length > 0) {
        return prev;
      }

      return {
        ...prev,
        currentRevision: initialCurrentRevision,
      };
    });
  }, [initialCurrentRevision]);

  useEffect(() => {
    const refetchPartnerships = async () => {
      if (
        showDialog &&
        !editingMultiple &&
        workingDonorSchedule?.donorId &&
        workingDonorSchedule?.id
      ) {
        try {
          const partnershipData = await queryClient.fetchQuery(
            partnershipsOptions({
              donorId: workingDonorSchedule.donorId,
              donorScheduleId: workingDonorSchedule.id,
            }),
          );

          const partnership = partnershipData?.[0];

          if (partnership) {
            dispatch({
              type: PartnershipsActions.UPDATE_PARTNERSHIP,
              payload: [
                {
                  ...partnership,
                  donor: workingDonorSchedule?.donor,
                  donorId: workingDonorSchedule?.donorId,
                },
              ],
            });
          }
        } catch (error) {
          console.error('Failed to refetch partnerships:', error);
        }
      }
    };

    refetchPartnerships();
  }, [
    showDialog,
    editingMultiple,
    workingDonorSchedule?.donorId,
    workingDonorSchedule?.id,
    dispatch,
  ]);

  const isPartnership = useMemo(() => {
    const hasOwnership = workingDonorSchedule?.donor?.ownerships?.[0]?.id;
    const hasPartnerships =
      partnership.length > 0 &&
      (partnership[0].account1Id ||
        partnership[0].account2Id ||
        partnership[0].account3Id ||
        partnership[0].account4Id);
    return hasOwnership || hasPartnerships;
  }, [workingDonorSchedule?.donor?.ownerships, partnership]);

  const isOverAging60 = useMemo(() => {
    return !!workingDonorSchedule?.owner?.aging60;
  }, [workingDonorSchedule?.owner?.aging60]);

  const {
    isPastDue,
    ownerPastDue,
    partnersPastDue,
    hasAging60,
    hasOver90Days,
    isLocked,
    overrideActiveUntil,
  } = usePastDueStatus(
    workingDonorSchedule?.owner,
    partnership,
    workingDonorSchedule?.loadDonorPartners ===
      LOAD_DONOR_PARTNERS_OPTION_VALUES.NO,
  );

  const isPastDueSaveRestricted = isLocked;

  const isExport = useMemo(() => {
    const exportValue = workingDonorSchedule?.export;
    if (!exportValue) return false;
    if (isNoEmbryoForExportEnabled) {
      return exportValue !== EXPORT_NO_VALUE;
    }
    return true;
  }, [workingDonorSchedule, isNoEmbryoForExportEnabled]);

  const isLowSemenQty = useMemo(() => {
    return workingDonorSchedule?.semen?.quantity < 3;
  }, [workingDonorSchedule]);

  const semenRebillRequired = useMemo(() => {
    return workingDonorSchedule?.creditRebill;
  }, [workingDonorSchedule]);

  const title = useMemo(() => {
    if (selectedCount > 1) {
      const names = selectedDonorSchedules
        .map((ds: DonorSchedule) => ds?.name)
        .filter(Boolean)
        .join(', ');
      return `${t('common.editSelected', { count: selectedCount })}: ${names}`;
    }
    const donorName = workingDonorSchedule?.donor?.name;
    const sireName = workingDonorSchedule?.sire?.name;
    const formattedDate = workingDonorSchedule?.date
      ? format(workingDonorSchedule.date, 'MM/dd/yyyy')
      : undefined;
    const names =
      donorName && sireName
        ? `${donorName}${isPartnership ? '*' : ''} x ${sireName}`
        : donorName || sireName || '';
    if (names && formattedDate) {
      const formattedName = `${names} - ${formattedDate}`;
      if (!isDsDefaultSoEnabled) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_NAME,
          payload: formattedName,
        });
      }
      return formattedName;
    }
    const currentName =
      formattedDate || names || t('donorScheduling.newDonorSchedule');

    if (!isDsDefaultSoEnabled) {
      dispatch({
        type: DonorSchedulingActions.UPDATE_NAME,
        payload: currentName,
      });
    }
    return currentName;
  }, [
    dispatch,
    isDsDefaultSoEnabled,
    isPartnership,
    selectedCount,
    selectedDonorSchedules,
    t,
    workingDonorSchedule?.date,
    workingDonorSchedule?.donor?.name,
    workingDonorSchedule?.sire?.name,
  ]);

  useEffect(() => {
    if (!isDsDefaultSoEnabled) return;
    if (selectedCount <= 1) {
      dispatch({ type: DonorSchedulingActions.UPDATE_NAME, payload: title });
    }
  }, [isDsDefaultSoEnabled, title, selectedCount, dispatch]);

  const isSaveDisabled = useMemo(() => {
    if (editingMultiple) return false;
    else {
      const requiredFieldsMissing =
        !workingDonorSchedule?.date ||
        !workingDonorSchedule?.aspirationSiteId ||
        !workingDonorSchedule?.csrId ||
        !workingDonorSchedule?.fertilizationSiteId;

      const billAccountRequired =
        workingDonorSchedule?.loadDonorPartners ===
          LOAD_DONOR_PARTNERS_OPTION_VALUES.NO &&
        !workingDonorSchedule?.billingAccountId;

      return requiredFieldsMissing || billAccountRequired;
    }
  }, [
    editingMultiple,
    workingDonorSchedule?.date,
    workingDonorSchedule?.aspirationSiteId,
    workingDonorSchedule?.csrId,
    workingDonorSchedule?.fertilizationSiteId,
    workingDonorSchedule?.loadDonorPartners,
    workingDonorSchedule?.billingAccountId,
  ]);

  const hasUnsavedChanges = useMemo(() => {
    return (
      JSON.stringify(workingDonorSchedule) !== JSON.stringify(lastSavedState)
    );
  }, [workingDonorSchedule, lastSavedState]);

  const trackedBulkEditChanges = useMemo(() => {
    if (!editingMultiple || !mergedDonorSchedules) return {};
    return getDifferencesOnly(mergedDonorSchedules, workingDonorSchedule);
  }, [editingMultiple, mergedDonorSchedules, workingDonorSchedule]);

  const awaitConfirmDialog = useCallback(
    (options: Parameters<typeof confirmDialog>[0]): Promise<boolean> =>
      new Promise((resolve) => {
        confirmDialog({
          ...options,
          accept: () => resolve(true),
          reject: () => resolve(false),
        });
      }),
    [],
  );

  const pickPayload = useCallback(
    (data: any) =>
      data?.upsertDonorSchedules ?? data?.editBulkDonorSchedules ?? null,
    [],
  );

  const handleSaveDonorSchedule = useCallback(async () => {
    try {
      setLastSavedState(workingDonorSchedule);
      const donorScheduleInput = {
        ...workingDonorSchedule,
        revision: revisionNumber.currentRevision,
        revisionComment: revisionComment,
        annotation: {
          ...workingDonorSchedule?.annotation,
          noteText: revisionComment,
        },
      };
      let newSchedule;
      if (editingMultiple) {
        if (Object.keys(trackedBulkEditChanges).length === 0) {
          dispatch(
            addToast({
              severity: 'warn',
              summary: t('donorScheduling.noChanges'),
              detail: t('donorScheduling.noChangesDetectedBulkEdit'),
              life: ToastLife.SHORT,
            }),
          );
          return;
        }

        const confirmed = await awaitConfirmDialog({
          message: (
            <div>
              <div>{t('donorScheduling.bulkEditChangesApplied')}</div>
              <ul style={{ marginLeft: 20 }}>
                {(() => {
                  const labelKeySet = new Set<string>();
                  const labels: string[] = [];
                  for (const key of Object.keys(trackedBulkEditChanges)) {
                    const labelKey =
                      BULK_EDIT_FIELD_LABEL_KEYS[key] ??
                      'donorScheduling.bulkEdit.fallback';
                    const label =
                      labelKey === 'donorScheduling.bulkEdit.fallback'
                        ? t(labelKey, { field: key })
                        : t(labelKey);
                    if (!labelKeySet.has(labelKey)) {
                      labelKeySet.add(labelKey);
                      labels.push(label);
                    }
                  }
                  return labels.map((label, index) => (
                    <li key={index}>{label}</li>
                  ));
                })()}
              </ul>
              <div>{t('donorScheduling.doYouWantToProceed')}</div>
            </div>
          ),
          header: t('donorScheduling.editingMultipleDonorSchedules'),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: t('common.yes'),
          rejectLabel: t('common.no'),
          acceptClassName: 'p-button-warning',
          rejectClassName: 'p-button-secondary',
        });

        if (!confirmed) return; // user clicked "No"

        newSchedule = await editBulkDonorSchedules({
          input: trackedBulkEditChanges as DonorSchedule,
          bulkUpdateIds:
            selectedDonorSchedules.length > 1
              ? selectedDonorSchedules.map((ds: DonorSchedule) => ds.id)
              : undefined,
        });
      } else {
        const cleanedInput = removeNulls(donorScheduleInput, allowedNullFields);
        newSchedule = await upsertDonorSchedules({
          input: cleanedInput as DonorSchedule,
          bulkUpdateIds:
            selectedDonorSchedules.length > 1
              ? selectedDonorSchedules.map((ds: DonorSchedule) => ds.id)
              : undefined,
        });
      }

      const payload = pickPayload(newSchedule?.data);
      const resultCode: number | undefined = payload?.code;
      const resultMessage: string | undefined = payload?.message;
      const newDonorScheduleId: string | undefined =
        payload?.donorSchedules?.[0]?.id;

      const caningOption = workingDonorSchedule?.billAccountCaningOption;
      const loadDonorPartners = workingDonorSchedule?.loadDonorPartners;
      const shouldUpdate =
        (caningOption === BILL_ACCOUNT_CANING_OPTION_VALUES.GROUPS_OF_THREE ||
          caningOption === BILL_ACCOUNT_CANING_OPTION_VALUES.ODDS_AND_EVENS) &&
        loadDonorPartners === LOAD_DONOR_PARTNERS_OPTION_VALUES.NO &&
        caningOption !== prevBillAccountCaningOption;

      if (shouldUpdate) {
        const idsToUpdate =
          selectedDonorSchedules.length > 1
            ? selectedDonorSchedules
                .map((ds: DonorSchedule) => ds.id)
                .filter(Boolean)
            : [newDonorScheduleId ?? workingDonorSchedule?.id].filter(Boolean);

        for (const donorScheduleId of idsToUpdate) {
          await updateBillAccountCaningGroups({
            variables: {
              donorScheduleId,
            },
          });
        }

        setPrevBillAccountCaningOption(
          workingDonorSchedule?.billAccountCaningOption,
        );
      }

      dispatch({
        type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
      });

      if (!editingMultiple && newDonorScheduleId) {
        dispatch({
          type: DonorSchedulingActions.UPDATE_ID,
          payload: newDonorScheduleId,
        });
      }

      if (resultCode === 200) {
        dispatch(
          addToast({
            severity: 'success',
            summary:
              selectedDonorSchedules.length > 1
                ? t('donorScheduling.bulkSchedulesSaved')
                : t('donorScheduling.donorScheduleSaved'),
            detail:
              selectedDonorSchedules.length > 1
                ? t('donorScheduling.bulkSchedulesSavedSuccessfully')
                : t('donorScheduling.scheduleSavedSuccessfully', {
                    name: workingDonorSchedule.name,
                  }),
            life: ToastLife.DEFAULT,
          }),
        );
        return {
          success: true,
          newDonorScheduleId: newDonorScheduleId ?? null,
        };
      } else {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('donorScheduling.saveFailed'),
            detail:
              selectedDonorSchedules.length > 1
                ? t('donorScheduling.failedToSaveBulkSchedules', {
                    message: resultMessage ?? '',
                  })
                : t('donorScheduling.failedToSaveSchedule', {
                    name: workingDonorSchedule.name,
                    message: resultMessage ?? '',
                  }),
            life: ToastLife.DEFAULT,
          }),
        );
        return {
          success: false,
          newDonorScheduleId: newDonorScheduleId ?? null,
        };
      }
    } catch (error) {
      console.error('Failed to save donorSchedule:', error);
      dispatch(
        addToast({
          severity: 'error',
          summary: t('donorScheduling.saveFailed'),
          detail:
            selectedDonorSchedules.length > 1
              ? t('donorScheduling.failedToSaveBulkSchedules', {
                  message: error?.toString(),
                })
              : t('donorScheduling.failedToSaveSchedule', {
                  name: workingDonorSchedule.name,
                  message: error?.toString(),
                }),
          life: ToastLife.DEFAULT,
        }),
      );
      return { success: false, newDonorScheduleId: null };
    }
  }, [
    workingDonorSchedule,
    revisionNumber.currentRevision,
    revisionComment,
    editingMultiple,
    pickPayload,
    prevBillAccountCaningOption,
    dispatch,
    trackedBulkEditChanges,
    awaitConfirmDialog,
    t,
    editBulkDonorSchedules,
    selectedDonorSchedules,
    allowedNullFields,
    upsertDonorSchedules,
    updateBillAccountCaningGroups,
  ]);

  const validatePartnershipAccounts = useCallback(() => {
    const isBillPartnership =
      workingDonorSchedule?.loadDonorPartners ===
      LOAD_DONOR_PARTNERS_OPTION_VALUES.YES;

    if (!isBillPartnership || !partnership?.[0]) {
      return true;
    }

    const accountsWithPercentages = [
      partnership[0].percent1,
      partnership[0].percent2,
      partnership[0].percent3,
      partnership[0].percent4,
    ].filter((percent) => percent != null && percent > 0).length;

    const accountsWithIds = [
      partnership[0].account1Id,
      partnership[0].account2Id,
      partnership[0].account3Id,
      partnership[0].account4Id,
    ].filter((id) => id != null).length;

    if (
      accountsWithPercentages > 0 &&
      accountsWithIds !== accountsWithPercentages
    ) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('donorScheduling.savePartnership'),
          detail:
            t('donorScheduling.incompletePartnershipAccounts', {
              required: accountsWithPercentages,
              provided: accountsWithIds,
            }) ||
            `Partnership requires ${accountsWithPercentages} accounts but only ${accountsWithIds} are set`,
          life: ToastLife.SHORT,
        }),
      );
      return false;
    }

    return true;
  }, [dispatch, partnership, workingDonorSchedule?.loadDonorPartners, t]);

  const validateDonorSireSpecies = useCallback(() => {
    if (!isPreventDonorSireMismatchEnabled) return true;

    const donorSpecies = normalizeSubFamily(
      workingDonorSchedule?.donor?.breed?.subFamily,
    );
    const sireSpecies = normalizeSubFamily(
      workingDonorSchedule?.sire?.breed?.subFamily,
    );

    if (!donorSpecies || !sireSpecies) return true;
    if (donorSpecies === sireSpecies) return true;

    dispatch(
      addToast({
        severity: 'error',
        summary: t('donorScheduling.speciesMismatch', {
          defaultValue: 'Species Mismatch',
        }),
        detail: t('donorScheduling.speciesMismatchDetail', {
          defaultValue:
            'The selected sire must match the donor species. Please select a sire with the same species as the donor.',
        }),
        life: ToastLife.DEFAULT,
      }),
    );
    return false;
  }, [
    dispatch,
    isPreventDonorSireMismatchEnabled,
    workingDonorSchedule?.donor?.breed?.subFamily,
    workingDonorSchedule?.sire?.breed?.subFamily,
    t,
  ]);

  const handleSave = useCallback(async () => {
    if (id !== '-1') {
      if (!validatePartnershipAccounts()) {
        return;
      }
    }

    if (!validateDonorSireSpecies()) {
      return;
    }

    if (id === '-1' && isPastDue) {
      const confirmed = await awaitConfirmDialog({
        message: t('donorScheduling.pastDueWarningMessage', {
          defaultValue:
            'This account is past due. Are you sure you want to create a donor schedule for this account?',
        }),
        header: t('donorScheduling.pastDueWarning', {
          defaultValue: 'Past Due Account Warning',
        }),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: t('common.yes'),
        rejectLabel: t('common.no'),
        acceptClassName: 'p-button-warning',
        rejectClassName: 'p-button-secondary',
      });

      if (!confirmed) return;
    }

    const result = await handleSaveDonorSchedule();

    if (result?.success && result.newDonorScheduleId) {
      const newParams = new URLSearchParams(params);
      newParams.set('id', result.newDonorScheduleId);
      newParams.delete('multiple');
      setParams(newParams);
    }
  }, [
    handleSaveDonorSchedule,
    params,
    setParams,
    validatePartnershipAccounts,
    validateDonorSireSpecies,
    id,
    isPastDue,
    awaitConfirmDialog,
    t,
  ]);

  useEffect(() => {
    const saveInterval = setInterval(async () => {
      if (
        hasUnsavedChanges &&
        id !== '-1' &&
        isPanelCollapsed &&
        workingDonorSchedule?.date &&
        workingDonorSchedule?.owner &&
        !isPastDueSaveRestricted &&
        !soScheduleDeactivated &&
        !isRevisionMode &&
        !(
          isPreventDonorSireMismatchEnabled &&
          workingDonorSchedule?.donor?.breed?.subFamily &&
          workingDonorSchedule?.sire?.breed?.subFamily &&
          normalizeSubFamily(workingDonorSchedule?.donor?.breed?.subFamily) !==
            normalizeSubFamily(workingDonorSchedule?.sire?.breed?.subFamily)
        )
      ) {
        try {
          await handleSave();
          setLastSavedState(workingDonorSchedule);
        } catch (error) {
          console.error('Auto-save failed:', error);
        }
      }
    }, 3000);

    return () => clearInterval(saveInterval);
  }, [
    handleSave,
    hasUnsavedChanges,
    id,
    isPastDueSaveRestricted,
    selectedCount,
    workingDonorSchedule,
    isPanelCollapsed,
    soScheduleDeactivated,
    params,
    isRevisionMode,
  ]);

  const handleCollapseChange = (collapsed: boolean) => {
    if (workingDonorSchedule?.donorId) {
      setIsPanelCollapsed(collapsed);
    } else {
      dispatch(
        addToast({
          severity: 'warn',
          summary: t('donorScheduling.selectDonor'),
          detail: t('donorScheduling.selectDonorDetail'),
          life: ToastLife.SHORT,
        }),
      );
    }
  };

  const handleCancel = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete('edit');
    newParams.delete('multiple');
    if (isRevisionMode) {
      newParams.delete('id');
    }
    let reset = false;
    if (!selectedDonorSchedules[0]?.id) {
      dispatch({
        type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
      });
      dispatch({
        type: PartnershipsActions.RESET_PARTNERSHIP,
      });
    }
    reset = true;
    if (reset) {
      newParams.set('reset', 'true');
    }
    setParams(newParams);
  };

  const handleSaveAndClose = () => {
    const missingFields = getMissingFields();

    if (missingFields.length > 0) {
      setShowConfirmationDialog(true);
    } else {
      proceedWithSaveAndClose();
    }
  };

  const proceedWithSaveAndClose = async () => {
    if (id !== '-1') {
      if (!validatePartnershipAccounts()) {
        return;
      }
    }

    if (!validateDonorSireSpecies()) {
      return;
    }

    const result = await handleSaveDonorSchedule();
    if (result?.success) {
      handleCancel();
    }
  };

  const handleConfirmationDialogClose = () => {
    setShowConfirmationDialog(false);
  };

  const getAutoSaveIcon = useCallback(() => {
    if (selectedCount > 1 && id === '-1') return 'pi-ban';
    if (id === '-1') return 'pi-ban';
    if (isPastDueSaveRestricted) return 'pi-ban';
    if (soScheduleDeactivated) return 'pi-ban';
    if (params.get('revision') === 'true') return 'pi-ban';
    return isPanelCollapsed ? 'pi-check' : 'pi-ban';
  }, [
    selectedCount,
    id,
    isPastDueSaveRestricted,
    soScheduleDeactivated,
    params,
    isPanelCollapsed,
  ]);

  const getAutoSaveMessage = useCallback(() => {
    if (selectedCount > 1 && id === '-1') {
      return t('donorScheduling.autosaveDisabledBulkEdit');
    }
    if (id === '-1') {
      return t('donorScheduling.autosaveDisabledUntilCreated');
    }
    if (isPastDueSaveRestricted) {
      return t('donorScheduling.autosaveDisabledPastDue');
    }
    if (soScheduleDeactivated) {
      return t('donorScheduling.autosaveDisabledUntilSoSchedule');
    }
    if (params.get('revision') === 'true') {
      return t('donorScheduling.autosaveDisabledRevisionMode');
    }
    return isPanelCollapsed
      ? t('donorScheduling.autosaveEnabled')
      : t('donorScheduling.autosaveDisabled');
  }, [
    selectedCount,
    id,
    isPastDueSaveRestricted,
    soScheduleDeactivated,
    params,
    isPanelCollapsed,
    t,
  ]);

  const handleCreateSoSchedule = useCallback(async () => {
    await handleSave();
    await runNewWorkflow(
      [{ RecordId: workingDonorSchedule?.id }],
      WORKFLOW.SOSchedule.id,
    );
    dispatch(
      addToast({
        severity: 'success',
        summary: t('donorScheduling.workflowExecuted'),
        detail: t('donorScheduling.soScheduleWorkflowCompleted'),
        life: ToastLife.DEFAULT,
      }),
    );
    setUnlockAll(false);
    setSoScheduleDeactivated(false);
  }, [
    handleSave,
    workingDonorSchedule,
    dispatch,
    setUnlockAll,
    setSoScheduleDeactivated,
  ]);

  const handleSoScheduleDialogYes = useCallback(async () => {
    const deactivateSoScheduleResponse = await deactivateSoSchedule({
      donorScheduleId: workingDonorSchedule?.id,
    }).unwrap();
    if (deactivateSoScheduleResponse?.deactivateSoSchedule === true) {
      if (setSoScheduleDeactivated) {
        setSoScheduleDeactivated(true);
      }
      if (setUnlockAll) {
        setUnlockAll(true);
      }
      dispatch({
        type: DonorSchedulingActions.UPDATE_SO_SCHEDULE,
        payload: null,
      });
    }
  }, [
    deactivateSoSchedule,
    workingDonorSchedule,
    setSoScheduleDeactivated,
    setUnlockAll,
    dispatch,
  ]);

  const renderFooter = () => {
    return (
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <i className={`pi ${getAutoSaveIcon()} flex items-center text-sm`} />
          <span className="text-500 flex items-center text-sm">
            {getAutoSaveMessage()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {soScheduleDeactivated ? (
            <Button
              label={t('donorScheduling.createSoSchedule')}
              icon="pi pi-plus"
              onClick={handleCreateSoSchedule}
              className="p-button-text"
            />
          ) : (
            <>
              <Button
                label={
                  id === '-1' && !editingMultiple
                    ? t('common.cancel')
                    : t('common.saveAndClose')
                }
                icon="pi pi-times"
                onClick={
                  id === '-1' && !editingMultiple
                    ? handleCancel
                    : handleSaveAndClose
                }
                className="p-button-text"
              />
              <Button
                label={
                  id === '-1' && selectedCount <= 1
                    ? t('common.create')
                    : t('common.save')
                }
                icon={
                  id === '-1' && selectedCount <= 1
                    ? 'pi pi-plus'
                    : 'pi pi-check'
                }
                onClick={handleSave}
                disabled={
                  isSaveDisabled || (isPastDueSaveRestricted && id !== '-1')
                }
                autoFocus
              />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderConfirmationDialogFooter = () => {
    return (
      <>
        <div className="rounded-lg p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <i className="pi pi-info-circle mt-0.5"></i>
              <p className="m-0 text-left text-sm">
                {t('donorScheduling.completeMissingFieldsEachDonor')}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                label={t('common.no')}
                icon="pi pi-times"
                onClick={handleConfirmationDialogClose}
                className="p-button-text"
              />
              <Button
                label={t('common.yes')}
                icon="pi pi-check"
                onClick={() => {
                  handleConfirmationDialogClose();
                  proceedWithSaveAndClose();
                }}
                autoFocus
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  const handleTabChange = (e: TabViewTabChangeEvent) => {
    setActiveIndex(e.index);
  };

  return (
    <>
      <Dialog
        ref={dialogRef}
        header={title || t('donorScheduling.newDonorSchedule')}
        visible={showDialog}
        style={{ width: '60vw' }}
        onHide={handleCancel}
        footer={renderFooter()}
        className={`edit-donor-schedule-dialog ${className}`}
        onShow={() =>
          setDialogContainer(dialogRef.current?.getMask() ?? undefined)
        }
      >
        {isExport && (
          <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
            <span>{t('donorScheduling.embryoForExport')}</span>
          </div>
        )}
        {isLowSemenQty && (
          <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
            <span>{t('donorScheduling.lowSemenQty')}</span>
          </div>
        )}
        {semenRebillRequired && (
          <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
            <span>{t('donorScheduling.semenRebillRequired')}</span>
          </div>
        )}
        {params.get('revision') === 'true' && (
          <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
            <span>{t('donorScheduling.revisionMode')}</span>
          </div>
        )}
        {isPastDue && (hasOver90Days || isLocked) ? (
          <div className="mb-4 rounded-lg bg-red-500 p-4 text-center font-semibold text-white">
            <span>
              {isLocked
                ? hasOver90Days
                  ? 'Account or partner is over 90 days and is restricted/closed'
                  : 'Account or partner is restricted/closed (TOG 90-day status)'
                : overrideActiveUntil
                  ? `Account or partner is over 90 days but override is active until ${overrideActiveUntil}`
                  : partnersPastDue && !ownerPastDue
                    ? 'Past Due Account (On Partnership)'
                    : t('donorScheduling.pastDue')}
            </span>
          </div>
        ) : isPastDue || isOverAging60 ? (
          <div className="mb-4 rounded-lg bg-yellow-400 p-4 text-center font-semibold text-black">
            <span>
              {hasAging60
                ? t('donorScheduling.overAging60')
                : t('donorScheduling.pastDue')}
            </span>
          </div>
        ) : null}
        <TabView
          className={tabViewClassName}
          activeIndex={activeIndex}
          onTabChange={handleTabChange}
        >
          <TabPanel header={t('common.general')}>
            <DonorScheduleFormGeneral
              className="flex flex-col gap-7"
              dialogContainer={dialogContainer}
              collapsed={isPanelCollapsed}
              viewOnly={false}
              onCollapseChange={handleCollapseChange}
              unlockAll={unlockAll}
              setUnlockAll={setUnlockAll}
              handleSoScheduleDialogYes={handleSoScheduleDialogYes}
              revisionComment={revisionComment}
              setRevisionComment={setRevisionComment}
              revisionNumber={revisionNumber}
              setRevisionNumber={setRevisionNumber}
            />
          </TabPanel>
          <TabPanel header={t('embryo.embryos')}>
            <DonorScheduleFormEmbryos
              className="flex flex-col gap-7"
              collapsed={isPanelCollapsed}
              viewOnly={false}
              onCollapseChange={handleCollapseChange}
            />
          </TabPanel>
          <TabPanel header={t('donorScheduling.schedules')}>
            <DonorScheduleFormSchedules
              className="flex flex-col gap-7"
              collapsed={isPanelCollapsed}
              viewOnly={false}
              onCollapseChange={handleCollapseChange}
              unlockAll={unlockAll}
              setUnlockAll={setUnlockAll}
              handleSoScheduleDialogYes={handleSoScheduleDialogYes}
            />
          </TabPanel>
          <TabPanel header={t('semen.semen')}>
            <DonorScheduleFormSemen
              className="flex flex-col gap-7"
              collapsed={isPanelCollapsed}
              viewOnly={false}
              onCollapseChange={handleCollapseChange}
              revisionComment={revisionComment}
              setRevisionComment={setRevisionComment}
              revisionNumber={revisionNumber}
              setRevisionNumber={setRevisionNumber}
            />
          </TabPanel>
          <TabPanel header={t('common.notes')}>
            <DonorScheduleFormNotes
              className="flex flex-col gap-7"
              viewOnly={false}
              revisionComment={revisionComment}
              setRevisionComment={setRevisionComment}
              revisionNumber={revisionNumber}
              setRevisionNumber={setRevisionNumber}
            />
          </TabPanel>
        </TabView>
      </Dialog>
      <MissingFieldsDialog
        visible={showConfirmationDialog}
        onHide={handleConfirmationDialogClose}
        selectedDonorSchedules={[workingDonorSchedule]}
        renderFooter={renderConfirmationDialogFooter}
      />
    </>
  );
};
